/**
 * Attendance Rate — ditore / javore / mujore.
 *
 * Shifrat vijnë të gatshme nga `GET /api/reports/attendance-rate`: norma,
 * orët e pritura dhe të punuara llogariten në server (services/attendanceRate.js)
 * me trajtimin e dokumentuar te docs/notes/attendance_rate.md. Ky ekran vetëm
 * zgjedh periudhën dhe i shfaq — asnjë formulë nuk rikrijohet këtu.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  ToggleButton, ToggleButtonGroup, TextField, MenuItem, IconButton, Tooltip, LinearProgress, Chip, Stack, Collapse, Button,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { authFetch } from '../../services/apiServices';
import { ReportPage, ReportFilters, StatTiles, ReportCard, ExportMenu, EmptyRow, LoadingRow } from '../report/ReportKit';

const PERIODS = [
  { key: 'daily', label: 'Ditore' },
  { key: 'weekly', label: 'Javore' },
  { key: 'monthly', label: 'Mujore' },
];

/** Dita e sotme e browser-it si YYYY-MM-DD (ora lokale, jo UTC). */
function todayYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** Zhvendos datën me një periudhë (±1 ditë / ±7 ditë / ±1 muaj), pa UTC. */
export function shiftDate(ymd, period, dir) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = period === 'monthly' ? new Date(y, m - 1 + dir, 1) : new Date(y, m - 1, d + dir * (period === 'weekly' ? 7 : 1));
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
const fmtDMY = (s) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : '—');
const fmtH = (v) => (v == null ? '—' : `${Number(v).toFixed(1)} h`);
const fmtRate = (v) => (v == null ? '—' : `${Number(v).toFixed(1)} %`);
const rateTone = (v) => (v == null ? undefined : v >= 95 ? 'success' : v >= 80 ? 'warning' : 'error');
const SOURCE_LABEL = { worker: 'Orari i vet', company: 'Orari i kompanisë', default: 'Pa orar · 8h H–P' };

function RateBar({ value }) {
  if (value == null) return <Typography variant="caption" color="text.disabled">—</Typography>;
  const tone = rateTone(value);
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 140 }}>
      <LinearProgress variant="determinate" value={Math.min(100, value)} color={tone} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
      <Typography variant="caption" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: `${tone}.main`, width: 52, textAlign: 'right' }}>{fmtRate(value)}</Typography>
    </Stack>
  );
}

export default function AttendanceRateReport() {
  const [period, setPeriod] = useState('monthly');
  const [date, setDate] = useState(todayYMD());
  // Filtrat: projekt / departament / grup / menaxher (+ thellësia). Të gjithë
  // zbatohen në server dhe priten me fushëveprimin e përdoruesit — opsionet
  // vijnë nga /options, po ashtu të kufizuara.
  const [departmentId, setDepartmentId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [managerDepth, setManagerDepth] = useState('all');
  const [options, setOptions] = useState({ projects: [], departments: [], groups: [], managers: [], scope: null });
  const departments = options.departments;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPolicy, setShowPolicy] = useState(false);

  useEffect(() => {
    authFetch('/api/reports/attendance-rate/options')
      .then((r) => r.json())
      .then((j) => { if (j?.success && j.data) setOptions({ projects: [], departments: [], groups: [], managers: [], ...j.data }); })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = new URLSearchParams({
        period, date,
        ...(departmentId ? { department_id: departmentId } : {}),
        ...(projectId ? { project_id: projectId } : {}),
        ...(groupId ? { group_id: groupId } : {}),
        ...(managerId ? { manager_id: managerId, manager_depth: managerDepth } : {}),
      }).toString();
      const res = await authFetch(`/api/reports/attendance-rate?${qs}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setData(json.data);
    } catch (e) { setData(null); setError(e.message || 'Gabim'); }
    finally { setLoading(false); }
  }, [period, date, departmentId, projectId, groupId, managerId, managerDepth]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => (data?.employees || []).slice().sort((a, b) => (a.attendance_rate ?? 101) - (b.attendance_rate ?? 101)), [data]);

  const exportCsv = () => {
    if (!data) return;
    const head = ['Punonjësi', 'Orari', 'Orë të pritura', 'Orë të punuara', 'Norma %', 'Leje (h)', 'Mungesë (h)', 'Orë shtesë (h)', 'Ditë pune', 'Prezent', 'Mungon', 'Leje'];
    const lines = rows.map((r) => [r.name, SOURCE_LABEL[r.schedule_source] || r.schedule_source || '', r.expected_hours, r.attended_hours, r.attendance_rate ?? '', r.leave_hours, r.absent_hours, r.overtime_hours, r.work_days, r.present_days, r.absent_days, r.leave_days]);
    const csv = [head, ...lines].map((l) => l.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `attendance-rate-${period}-${date}.csv`; a.click();
  };

  const p = data?.period;
  const tiles = data ? [
    { label: 'Attendance Rate', value: fmtRate(data.attendance_rate), accent: true, tone: rateTone(data.attendance_rate) },
    { label: 'Orë të pritura', value: fmtH(data.expected_hours) },
    { label: 'Orë të punuara', value: fmtH(data.attended_hours) },
    { label: 'Punonjës', value: String(data.employee_count) },
    { label: 'Leje (të justifikuara)', value: fmtH(data.leave_hours) },
    { label: 'Mungesë', value: fmtH(data.absent_hours), tone: data.absent_hours > 0 ? 'error' : undefined },
  ] : [];

  return (
    <ReportPage>
      <ReportFilters onRefresh={load} actions={<ExportMenu onCsv={exportCsv} disabled={!data} />}>
        <ToggleButtonGroup size="small" exclusive value={period} onChange={(_, v) => v && setPeriod(v)} aria-label="Periudha">
          {PERIODS.map((x) => <ToggleButton key={x.key} value={x.key} sx={{ textTransform: 'none', px: 1.5 }}>{x.label}</ToggleButton>)}
        </ToggleButtonGroup>
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <Tooltip title="Periudha e mëparshme"><IconButton size="small" onClick={() => setDate((d) => shiftDate(d, period, -1))}><ChevronLeftIcon fontSize="small" /></IconButton></Tooltip>
          <TextField size="small" type="date" label="Data" InputLabelProps={{ shrink: true }} value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
          <Tooltip title="Periudha e radhës"><IconButton size="small" onClick={() => setDate((d) => shiftDate(d, period, 1))}><ChevronRightIcon fontSize="small" /></IconButton></Tooltip>
        </Stack>
        <TextField size="small" select label="Projekti" value={projectId} onChange={(e) => setProjectId(e.target.value)} inputProps={{ 'data-testid': 'filter-project' }}>
          <MenuItem value="">Të gjitha</MenuItem>
          {options.projects.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Departamenti" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} inputProps={{ 'data-testid': 'filter-department' }}>
          <MenuItem value="">Të gjithë</MenuItem>
          {departments.map((d) => <MenuItem key={d.Id ?? d.id} value={String(d.Id ?? d.id)}>{d.Name || d.name}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Grupi" value={groupId} onChange={(e) => setGroupId(e.target.value)} inputProps={{ 'data-testid': 'filter-group' }}>
          <MenuItem value="">Të gjithë</MenuItem>
          {options.groups.map((g) => <MenuItem key={g.id} value={String(g.id)}>{g.name}{g.members != null ? ` (${g.members})` : ''}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Menaxheri" value={managerId} onChange={(e) => setManagerId(e.target.value)} inputProps={{ 'data-testid': 'filter-manager' }}>
          <MenuItem value="">Të gjithë</MenuItem>
          {options.managers.map((m) => <MenuItem key={m.id} value={String(m.id)}>{m.name} ({m.direct_reports})</MenuItem>)}
        </TextField>
        {managerId && (
          <ToggleButtonGroup size="small" exclusive value={managerDepth} onChange={(_, v) => v && setManagerDepth(v)} aria-label="Thellësia">
            <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.25 }}>Gjithë hierarkia</ToggleButton>
            <ToggleButton value="direct" sx={{ textTransform: 'none', px: 1.25 }}>Vetëm direkt</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Button size="small" startIcon={<InfoOutlinedIcon />} onClick={() => setShowPolicy((v) => !v)} sx={{ textTransform: 'none' }}>Si llogaritet</Button>
      </ReportFilters>

      <Collapse in={showPolicy}>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.25, borderRadius: 2, bgcolor: 'action.hover' }}>
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.6 }}>
            <b>Norma = orë të punuara ÷ orë të pritura × 100.</b> Orët e pritura = orari i ditës (turni − pauzat e planifikuara) në ditët e punës
            sipas orarit, pa festa zyrtare, vetëm brenda punësimit dhe vetëm deri sot — <b>minus pushimi i aprovuar</b> (vjetor, mjekësor, lehoni,
            atësi, martesor, pa pagesë: të gjitha të justifikuara, jo mungesë). Kërkesat në pritje nuk justifikojnë. Trajnimi dhe pauzat e
            planifikuara janë punë; koha idle e agjentit nuk është prezencë (raportohet veçmas). Orët e punuara kufizohen në ditë te ato të
            pritura — orët shtesë raportohen veçmas dhe nuk e ngrenë normën mbi 100 %. Punëtori pa orar: orari i kompanisë, ndryshe 8 orë H–P.
          </Typography>
        </Paper>
      </Collapse>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}

      {data && (
        <>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1, rowGap: 0.5 }}>
            <Chip size="small" label={p.label || `${fmtDMY(p.from)} – ${fmtDMY(p.to)}`} sx={{ fontWeight: 700 }} />
            <Typography variant="caption" color="text.secondary">
              {fmtDMY(p.from)} – {fmtDMY(p.to)} · {p.days.elapsed}/{p.days.total} ditë të kaluara{p.elapsed_to && p.elapsed_to < p.to ? ` (deri më ${fmtDMY(p.elapsed_to)})` : ''} · zona {data.timezone}
            </Typography>
            {data.scope?.level && data.scope.level !== 'all' && (
              <Chip size="small" color="info" variant="outlined" label={`Fushëveprimi: ${data.scope.level === 'team' ? 'ekipi juaj' : data.scope.level === 'department' ? 'departamenti juaj' : 'vetëm ju'}`} />
            )}
            {data.holiday_hours > 0 && <Chip size="small" variant="outlined" label={`Festa: ${fmtH(data.holiday_hours)}`} />}
            {data.overtime_hours > 0 && <Chip size="small" variant="outlined" label={`Orë shtesë: ${fmtH(data.overtime_hours)}`} />}
            {data.idle_hours > 0 && <Chip size="small" variant="outlined" label={`Idle: ${fmtH(data.idle_hours)}`} />}
          </Stack>
          <StatTiles items={tiles} />

          <ReportCard title="Sipas punonjësit" count={rows.length} countLabel="punonjës">
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Punonjësi</TableCell>
                    <TableCell>Orari</TableCell>
                    <TableCell align="right">Pritura</TableCell>
                    <TableCell align="right">Punuara</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>Norma</TableCell>
                    <TableCell align="right">Leje</TableCell>
                    <TableCell align="right">Mungesë</TableCell>
                    <TableCell align="right">Shtesë</TableCell>
                    <TableCell align="center">Ditë (P / M / L)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && !rows.length && <LoadingRow colSpan={9} />}
                  {!loading && !rows.length && <EmptyRow colSpan={9} message={(projectId || departmentId || groupId || managerId) ? 'Asnjë punonjës nuk përputhet me këta filtra brenda fushëveprimit tuaj.' : 'Asnjë punonjës me orë të pritura ose të punuara në këtë periudhë.'} />}
                  {rows.map((r) => (
                    <TableRow key={r.worker_id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{SOURCE_LABEL[r.schedule_source] || '—'}</Typography></TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtH(r.expected_hours)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtH(r.attended_hours)}</TableCell>
                      <TableCell><RateBar value={r.attendance_rate} /></TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.leave_hours ? fmtH(r.leave_hours) : '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: r.absent_hours > 0 ? 'error.main' : 'text.secondary' }}>{r.absent_hours ? fmtH(r.absent_hours) : '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.overtime_hours ? fmtH(r.overtime_hours) : '—'}</TableCell>
                      <TableCell align="center" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.present_days} / {r.absent_days} / {r.leave_days}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </ReportCard>
        </>
      )}
    </ReportPage>
  );
}
